import { useEffect, useRef, useState } from 'react';

import createScheduler from '@/polyfills/create-scheduler';
import WorkerCalculations from './contrast-calculations.worker?worker';
import { CheckContrastCalculationsProps, ContrastCheckerProps } from './use-contrast-checkout.types';

type ContrastResult = {
  hasValidContrast: boolean;
  backgroundMode: 'light' | 'dark';
} | null;
const resultMap = new Map<string, ContrastResult>();
const getResultMemo = (props: Partial<ContrastCheckerProps>) => {
  return resultMap.get(JSON.stringify(props));
};
const setResultMemo = (props: Partial<ContrastCheckerProps>, result: ContrastResult) => {
  return resultMap.set(JSON.stringify(props), result);
};

const useContrastChecker = ({
  backgroundImageSrc,
  foregroundColor,
  ref,
  sampleRegion,
  sampleStep = 1,
  shouldRun = false,
}: ContrastCheckerProps) => {
  const [hasSufficientContrast, setHasSufficientContrast] = useState<{
    hasValidContrast: boolean;
    backgroundMode: 'light' | 'dark';
  } | null>(null);
  const resultRef = useRef(getResultMemo({ backgroundImageSrc, foregroundColor, sampleRegion }));

  useEffect(() => {
    if (resultRef.current) {
      setHasSufficientContrast(resultRef.current);
      return;
    }

    if (!shouldRun) return;
    if (!backgroundImageSrc) return;
    if (!sampleRegion) return;

    const el = ref?.current;
    if (!el) return;

    const worker = new WorkerCalculations();
    worker.onmessage = ({ data }) => {
      setHasSufficientContrast(data);
      setResultMemo({ backgroundImageSrc, foregroundColor, sampleRegion }, data);
    };

    const taskScheduler = createScheduler();
    taskScheduler.schedule(
      () => {
        const img = new Image();
        img.width = el.offsetWidth;
        img.height = el.offsetHeight;
        img.src = backgroundImageSrc;
        img.crossOrigin = 'Anonymous'; // Important for CORS if image is from another domain

        img.addEventListener('error', () => {
          console.error('Error loading background image for contrast check.');
          setHasSufficientContrast({ hasValidContrast: true, backgroundMode: 'dark' });
          worker?.terminate();
        });

        const onImageLoad = async () => {
          // first send props
          worker.postMessage({ sampleRegion, sampleStep, foregroundColor } satisfies CheckContrastCalculationsProps);

          const offscreen = document.createElement('canvas').transferControlToOffscreen();
          worker.postMessage({ canvas: offscreen }, [offscreen]);

          const bitmap = await createImageBitmap(img);
          worker.postMessage({ imageBitmap: bitmap }, [bitmap]);
        };

        img.addEventListener('load', onImageLoad);
      },
      {
        delay: 0,
        priority: 'background',
      }
    );

    return () => {
      worker.terminate();
    };
    // intentionally ignoring sampleStep, sampleRegion,and ref, becaue they make this render too many times and this is quite
    // an expensive process, so we want to minimize the amount of times we calculate.
    // only passing in these specific items ensures we don't re-run this when the parent component re-renders, it only runs once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [backgroundImageSrc, foregroundColor]);

  return hasSufficientContrast;
};

export default useContrastChecker;
